import { useAuth } from '../context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { api, API_BASE } from '../lib/api';
import { TID } from '../constants/testIds';

export default function AdminLeads() {
    const { user, loading } = useAuth();
    const { data: stats } = useQuery({
        queryKey: ['admin-stats'],
        queryFn: async () => (await api.get('/admin/stats')).data,
        enabled: !!user?.is_admin,
    });
    const { data: leads } = useQuery({
        queryKey: ['admin-leads'],
        queryFn: async () => (await api.get('/admin/leads')).data,
        enabled: !!user?.is_admin,
    });

    if (loading) return <div className="p-16 text-neutral-500">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.is_admin) return <div className="max-w-2xl mx-auto p-16 text-neutral-500">Admin access required.</div>;

    return (
        <div>
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-8 flex items-end justify-between">
                    <div>
                        <div className="overline">Admin</div>
                        <h1 className="font-editorial text-[40px] lg:text-[52px] tracking-editorial mt-4">Leads</h1>
                    </div>
                    <a
                        data-testid={TID.admin.exportCsv}
                        href={`${API_BASE}/admin/leads/export.csv`}
                        className="inline-flex items-center gap-2 border border-brand-navy bg-brand-navy text-white px-5 py-2.5 text-[13.5px] hover-invert hover:bg-brand-gold hover:text-brand-navy"
                       
                    >
                        Export CSV
                    </a>
                </div>
            </section>

            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 border-l border-t border-neutral-200">
                    {[
                        { l: 'Total leads', v: stats?.total_leads ?? '—' },
                        { l: 'Registered users', v: stats?.total_users ?? '—' },
                        { l: 'Chat sessions', v: stats?.total_chat_sessions ?? '—' },
                        { l: 'Watchlist alerts', v: stats?.total_watchlist ?? '—' },
                    ].map((s) => (
                        <div key={s.l} data-testid={TID.admin.statCard} className="border-r border-b border-neutral-200 p-6">
                            <div className="overline">{s.l}</div>
                            <div className="font-mono-data text-[28px] tabular mt-2">{s.v}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid={TID.admin.leadsTable}>
                    <div className="grid grid-cols-12 py-3 px-4 text-[11px] uppercase tracking-[0.18em] text-neutral-500 border-b border-neutral-200">
                        <div className="col-span-3">Email</div>
                        <div className="col-span-2">Name</div>
                        <div className="col-span-2">Source</div>
                        <div className="col-span-3">Tags / meta</div>
                        <div className="col-span-2">Created</div>
                    </div>
                    {(leads?.items || []).map((l) => (
                        <div key={l.lead_id} className="grid grid-cols-12 py-3 px-4 border-b border-neutral-100 text-[13.5px]">
                            <div className="col-span-3 font-mono-data">{l.email}</div>
                            <div className="col-span-2">{l.name || '—'}</div>
                            <div className="col-span-2 text-neutral-500">{l.source}</div>
                            <div className="col-span-3 text-neutral-500 truncate">{(l.tags || []).join(', ')}</div>
                            <div className="col-span-2 text-neutral-500 font-mono-data text-[12px]">{l.created_at?.slice(0, 16).replace('T', ' ')}</div>
                        </div>
                    ))}
                    {leads?.items?.length === 0 && (
                        <div className="p-10 text-neutral-500 text-center">No leads yet.</div>
                    )}
                </div>
            </section>
        </div>
    );
}
